import { useCallback, useEffect, useRef } from "react";
import { Bold, Italic, List, ListOrdered, Heading2, Underline, Image as ImageIcon } from "lucide-react";
import DOMPurify from "dompurify";
import { cn } from "@/lib/utils";

const ALLOWED_TAGS = ["p", "br", "b", "strong", "i", "em", "u", "ul", "ol", "li", "h2", "h3", "a"];
const ALLOWED_ATTR = ["href", "target", "rel"];

// HTML email bodies additionally allow images/GIFs and a wider set of
// formatting tags + inline styles. Kept in sync with the server-side
// sanitizer in server/services/email-html.ts.
const ALLOWED_EMAIL_TAGS = [
  "p", "br", "b", "strong", "i", "em", "u", "s", "span", "div",
  "ul", "ol", "li", "h1", "h2", "h3", "h4", "blockquote", "pre", "code",
  "a", "img", "hr", "table", "thead", "tbody", "tr", "td", "th",
];
const ALLOWED_EMAIL_ATTR = [
  "href", "target", "rel", "src", "alt", "title", "width", "height",
  "style", "align", "border", "cellpadding", "cellspacing",
];

export function sanitizeRichText(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });
}

export function sanitizeEmailHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ALLOWED_EMAIL_TAGS,
    ALLOWED_ATTR: ALLOWED_EMAIL_ATTR,
  });
}

type RichTextEditorProps = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  /** When true, shows an "Insert image/GIF" button and allows <img> tags. */
  allowImages?: boolean;
};

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  className,
  autoFocus,
  allowImages,
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastValueRef = useRef<string>(value);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value !== lastValueRef.current && value !== el.innerHTML) {
      el.innerHTML = value;
      lastValueRef.current = value;
    }
  }, [value]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value) el.innerHTML = value;
    if (autoFocus) {
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = useCallback((command: string, arg?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    const html = editorRef.current?.innerHTML ?? "";
    lastValueRef.current = html;
    onChange(html);
  }, [onChange]);

  const handleInput = () => {
    const html = editorRef.current?.innerHTML ?? "";
    lastValueRef.current = html;
    onChange(html);
  };

  const insertImage = useCallback(() => {
    const url = window.prompt("Image or GIF URL (https://…)");
    if (!url) return;
    const trimmed = url.trim();
    if (!/^https?:\/\//i.test(trimmed)) {
      window.alert("Please enter a full URL starting with http:// or https://");
      return;
    }
    const alt = window.prompt("Description (alt text), optional") ?? "";
    const safeUrl = trimmed.replace(/"/g, "&quot;");
    const safeAlt = alt.replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    exec(
      "insertHTML",
      `<img src="${safeUrl}" alt="${safeAlt}" style="max-width:100%;height:auto;" />`
    );
  }, [exec]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "b") {
      e.preventDefault();
      exec("bold");
    } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "i") {
      e.preventDefault();
      exec("italic");
    } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "u") {
      e.preventDefault();
      exec("underline");
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  return (
    <div className={cn("rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ring-offset-background", className)}>
      <div className="flex flex-wrap items-center gap-0.5 border-b px-1 py-1">
        <ToolbarButton title="Heading" onMouseDown={() => exec("formatBlock", "<h2>")}>
          <Heading2 className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Bold (Ctrl+B)" onMouseDown={() => exec("bold")}>
          <Bold className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Italic (Ctrl+I)" onMouseDown={() => exec("italic")}>
          <Italic className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Underline (Ctrl+U)" onMouseDown={() => exec("underline")}>
          <Underline className="h-3.5 w-3.5" />
        </ToolbarButton>
        <span className="mx-0.5 h-4 w-px bg-border" aria-hidden />
        <ToolbarButton title="Bulleted list" onMouseDown={() => exec("insertUnorderedList")}>
          <List className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Numbered list" onMouseDown={() => exec("insertOrderedList")}>
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarButton>
        {allowImages && (
          <>
            <span className="mx-0.5 h-4 w-px bg-border" aria-hidden />
            <ToolbarButton title="Insert image / GIF" onMouseDown={insertImage}>
              <ImageIcon className="h-3.5 w-3.5" />
            </ToolbarButton>
          </>
        )}
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        className={cn(
          "rich-text-content min-h-[120px] px-3 py-2 text-sm outline-none",
          "[&_h2]:mt-1 [&_h2]:mb-1 [&_h2]:text-base [&_h2]:font-semibold",
          "[&_ul]:my-1 [&_ul]:ml-5 [&_ul]:list-disc",
          "[&_ol]:my-1 [&_ol]:ml-5 [&_ol]:list-decimal",
          "[&_p]:my-0.5",
          "[&_a]:text-primary [&_a]:underline",
          "[&_img]:my-1 [&_img]:max-w-full [&_img]:rounded",
          "empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground"
        )}
      />
    </div>
  );
}

function ToolbarButton({
  children,
  title,
  onMouseDown,
}: {
  children: React.ReactNode;
  title: string;
  onMouseDown: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => {
        e.preventDefault();
        onMouseDown();
      }}
      className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      {children}
    </button>
  );
}

export function RichTextView({
  html,
  className,
}: {
  html: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rich-text-content text-sm",
        "[&_h2]:mt-1 [&_h2]:mb-1 [&_h2]:text-base [&_h2]:font-semibold",
        "[&_ul]:my-1 [&_ul]:ml-5 [&_ul]:list-disc",
        "[&_ol]:my-1 [&_ol]:ml-5 [&_ol]:list-decimal",
        "[&_p]:my-0.5",
        "[&_a]:text-primary [&_a]:underline",
        className
      )}
      dangerouslySetInnerHTML={{ __html: sanitizeRichText(html) }}
    />
  );
}

export function isRichTextEmpty(html: string | null | undefined): boolean {
  if (!html) return true;
  // Markup that carries meaning on its own (e.g. an image-only GIF email)
  // counts as non-empty even when there is no text.
  if (/<img\b/i.test(html)) return false;
  const stripped = html.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim();
  return stripped.length === 0;
}

/** Render a sanitized HTML email body (allows images/GIFs). */
export function EmailHtmlView({
  html,
  className,
}: {
  html: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rich-text-content text-sm",
        "[&_h1]:text-lg [&_h1]:font-semibold [&_h2]:text-base [&_h2]:font-semibold",
        "[&_ul]:my-1 [&_ul]:ml-5 [&_ul]:list-disc",
        "[&_ol]:my-1 [&_ol]:ml-5 [&_ol]:list-decimal",
        "[&_p]:my-1",
        "[&_a]:text-primary [&_a]:underline",
        "[&_img]:my-1 [&_img]:max-w-full [&_img]:rounded",
        className
      )}
      dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(html) }}
    />
  );
}
